// ── WHO "ADMIN" IS, IN A SUPPORT CHAT ────────────────────────────────────────
// A chat is between two uids, so "Contact admin" needs a concrete account.
//
// WHY IT CANNOT BE LOOKED UP. Admin is a custom CLAIM on the auth token, not a
// field on users/{uid} — deliberately, because a field is written by something
// and whatever can write it can grant itself the role (see firestore.rules).
// Claims are invisible to a Firestore query, so the account has to be named.
//
// NAMED IN THE DATABASE, NOT IN .env. It was an env var, which meant editing a
// file and rebuilding to switch the feature on — and a blank value hid the
// button with nothing in the app to say why. It is now `config/support`, set
// from the admin panel in one click and read by every client. See
// firebase/supportService.
//
// The env var is still honoured as a fallback, so an existing deployment that
// set one keeps working without a database write.

export const SUPPORT_ADMIN_UID_FALLBACK =
  (process.env.REACT_APP_SUPPORT_ADMIN_UID || '').trim();

// The picture the support account shows as. The logo is the app's own default
// admin image — the same mark used for the favicon and the PWA icon — so the
// conversation is recognisably from Tunify rather than from a stranger.
export const SUPPORT_AVATAR = '/images/Tunify Original logo tab.png';

/**
 * The participant record to open a support conversation with.
 *
 * The NAME is written into the chat document at creation, so it is what the
 * user sees in their list forever after. Translated at the call site and passed
 * in, rather than hard-coded here, because "Admin" is a word a reader sees.
 */
export function supportIdentity(uid, name) {
  return { uid, name, avatar: SUPPORT_AVATAR };
}

/**
 * Whether a support conversation can be opened.
 *
 * @param {string} adminUid the configured account
 * @param {string} myUid    the caller — an admin is never offered a chat with
 *                          themselves, which openChat would refuse anyway.
 */
export function hasSupportChat(adminUid, myUid) {
  return !!adminUid && !!myUid && adminUid !== myUid;
}

// ── HOW THE SUPPORT ACCOUNT IS SHOWN ─────────────────────────────────────────
// NOT the admin's own name and picture. Whoever is on duty is a real person
// with a real profile — the conversation in the screenshot opened as "Rick
// Sanches" — and a user writing to support should see the company, not an
// individual. It also means support can change hands without every existing
// conversation suddenly appearing to be from a stranger.
//
// Applied at RENDER time (see otherParticipant in ChatList), not just when the
// chat is created: the participant record may already have been written with
// the admin's real details by an earlier conversation, and overriding the
// display is the only thing that holds regardless of what is stored.
//
// Not translated. It is a brand name with a role after it, and "Tunify" does
// not change between locales.
export const SUPPORT_DISPLAY_NAME = 'Tunify Admin';
